import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Collapse } from '@mui/material';
import { ColDef, DragStoppedEvent, ICellRendererParams } from 'ag-grid-community';
import mainStyles from '../../pages/main.module.css';
import styles from './batchPdfDownloadModal.module.css';
import ScreenModal from '../ScreenModal/screenModal';
import Button from '../Button/button';
import Checkbox from '../Checkbox';
import { BaseTag } from '../../services/http/tag.api';
import CustomSwitch from '../CustomSwitch/CustomSwitch';
import TagFormat from '../../utils/tagFormat';
import { BatchPdfDownloadNameRules } from '../../services/http/documents.api';
import BaseTable from '../BaseTable';
import AutoCompleteCellRender from './AutoCompleteCellRender';

export interface BatchPdfDownloadModalProps {
  tags: BaseTag[];
  totalDocumentQuantity: number;
  onCancel: (e: React.MouseEvent<HTMLButtonElement>) => void
  onSubmit: (shouldIncludePdfFiles: boolean, shouldIncludePreviousVersions: boolean, shouldIncludeAttachmentFiles: boolean, nameRules?: BatchPdfDownloadNameRules) => void
}

const LOCAL_STORAGE_KEY = {
  SHOULD_USE_NAME_RULES: 'shouldUseNameRules',
  NAME_ITEMS: 'nameItems',
} as const;

const NAME_ITEM_TYPE = {
  SYSTEM: 1,
  TAG: 2,
  FOLDER: 3,
} as const;

const SYSTEM_NAME_ITEMS = [
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: '文書名',
    itemValue: 'documentName',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: '文書種類',
    itemValue: 'typeName',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: 'PDFファイル名',
    itemValue: 'fileName',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: '契約種別',
    itemValue: 'electronicFlg',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: '書面有無',
    itemValue: 'paperFlg',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: 'バーコード印字欄',
    itemValue: 'barcodePrinting',
  },
  {
    itemType: NAME_ITEM_TYPE.SYSTEM,
    label: '保管品バーコード',
    itemValue: 'itemCode',
  },
  {
    itemType: NAME_ITEM_TYPE.FOLDER,
    label: 'フォルダ名',
    itemValue: null,
  },
] as const;

interface NameItemState {
  itemType: number | null;
  itemValue: string | null;
  inputValue: string;
  key: string;
}

const getNewNameItem = (): NameItemState => ({
  itemType: null, itemValue: null, inputValue: '', key: uuid(),
});

const MAX_NAME_ITEMS = 10;

function BatchPdfDownloadModal(props: BatchPdfDownloadModalProps) {
  const {
    tags,
    totalDocumentQuantity,
    onCancel,
    onSubmit,
  } = props;
  const [shouldIncludePdfFiles, setShouldIncludePdfFiles] = useState(false);
  const [shouldIncludePreviousVersions, setShouldIncludePreviousVersions] = useState(false);
  const [shouldIncludeAttachmentFiles, setShouldIncludeAttachmentFiles] = useState(false);
  const [shouldUseNameRules, setShouldUseNameRules] = useState(() => {
    const defaultShouldUseNameRules = false;
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY.SHOULD_USE_NAME_RULES);
      return stored ? Boolean(JSON.parse(stored)) : defaultShouldUseNameRules;
    } catch {
      return defaultShouldUseNameRules;
    }
  });
  const [nameItems, setNameItems] = useState<NameItemState[]>([getNewNameItem()]);
  const [idPosition, setIdPosition] = useState<'start' | 'end'>('end');

  const tagNameItems = tags.map(({ tagSettingId, tagLabel, format }) => ({
    itemType: NAME_ITEM_TYPE.TAG,
    label: `${tagLabel}${format === TagFormat.DATE ? '(yyyymmdd)' : ''}`,
    itemValue: tagSettingId.toString(),
  }));

  const allFilenameItems = [
    ...tagNameItems,
    ...SYSTEM_NAME_ITEMS,
  ];

  const nameItemOptions = allFilenameItems
    .map((item, index) => {
      if (nameItems.some(({ itemType, itemValue }) => itemType === item.itemType && itemValue === item.itemValue)) return null;
      return {
        text: item.label,
        value: index.toString(),
      };
    })
    .filter(Boolean) as { text: string, value: string }[];

  const filenamePreview = useMemo(() => {
    const renderItem = (inputValue: string) => <span>{inputValue}</span>;
    const separator = <span>_</span>;
    const selectedNameItems = nameItems.map(({ inputValue }) => inputValue).filter(Boolean);
    const items = selectedNameItems.flatMap((inputValue, i) => (i < selectedNameItems.length - 1 ? [renderItem(inputValue), separator] : [renderItem(inputValue)]));
    return selectedNameItems.length ? (
      <div className={styles.filenamePreviewContainer}>
        {items}
        <span>_文書ID_版(v).pdf</span>
      </div>
    ) : null;
  }, [nameItems]);

  const handleChangeShouldIncludePdfFiles = (value: boolean) => {
    setShouldIncludePdfFiles(value);
    if (!value) setShouldIncludePreviousVersions(false);
  };

  const handleSelectNameItem = (index: number, value: string) => {
    const filenameItemIndex = Number(value);
    const filenameItem = allFilenameItems[filenameItemIndex];
    setNameItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        itemType: filenameItem.itemType,
        itemValue: filenameItem.itemValue,
        inputValue: filenameItem.label,
      };
      return next;
    });
  };

  const handleRemoveNameItem = (index: number) => setNameItems((prev) => prev.filter((_, i) => i !== index));

  const handleAddNameItem = () => setNameItems((prev) => [...prev, getNewNameItem()]);

  const handleSubmit = () => {
    const nameRules = shouldUseNameRules ? {
      nameItems: nameItems.map(({ itemType, itemValue }) => ({ itemType, itemValue })),
      idPosition,
    } as BatchPdfDownloadNameRules : undefined;
    localStorage.setItem(LOCAL_STORAGE_KEY.SHOULD_USE_NAME_RULES, JSON.stringify(shouldUseNameRules));
    if (shouldUseNameRules) localStorage.setItem(LOCAL_STORAGE_KEY.NAME_ITEMS, JSON.stringify(nameItems));
    else localStorage.removeItem(LOCAL_STORAGE_KEY.NAME_ITEMS);
    return onSubmit(shouldIncludePdfFiles, shouldIncludeAttachmentFiles, shouldIncludePreviousVersions, nameRules);
  };

  const showAddNameItemButton = nameItems.length < MAX_NAME_ITEMS;

  const submitButtonDisabled = (
    !shouldIncludePdfFiles && !shouldIncludeAttachmentFiles && !shouldIncludePreviousVersions
  ) || (shouldUseNameRules && (nameItems.length === 0 || nameItems.some(({ itemType }) => !itemType)));

  const columns: ColDef[] = useMemo(() => [
    {
      field: 'inputValue',
      rowDrag: true,
      flex: 1,
      suppressMenu: true,
      cellRendererSelector: (params: ICellRendererParams) => ({
        component: AutoCompleteCellRender,
        params: {
          label: `項目${params.rowIndex + 1}`,
          options: nameItemOptions,
          onSelect: (index: number, selected: typeof nameItemOptions[number]) => handleSelectNameItem(index, selected.value),
          onRemove: nameItems.length > 1 ? handleRemoveNameItem : undefined,
        },
      }),
    },
  ], [nameItems]);

  const onDragStopped = (event: DragStoppedEvent) => {
    const reorderedData: NameItemState[] = [];
    event.api.forEachNode((node, index) => {
      reorderedData.push({ ...node.data, order: index + 1 });
    });
    setNameItems(reorderedData);
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY.NAME_ITEMS);
      if (!stored) return;
      const parsed: NameItemState[] = JSON.parse(stored);
      const restoredNameItems = parsed.map((nameItem) => {
        const filenameItem = allFilenameItems.find(({ itemType, itemValue }) => itemType === nameItem.itemType && itemValue === nameItem.itemValue);
        if (!filenameItem) return null;
        return {
          itemType: filenameItem.itemType,
          itemValue: filenameItem.itemValue,
          inputValue: filenameItem.label,
          key: uuid(),
        };
      }).filter(Boolean) as NameItemState[];
      if (restoredNameItems.length === 0) {
        setNameItems([getNewNameItem()]);
      } else {
        setNameItems(restoredNameItems);
      }
    } catch (e) {
      console.error(e);
    }
  }, [tags]);

  const nameRulesForm = (
    <div className={styles.nameRulesContainer}>
      <div className={styles.nameRulesFormRow}>
        <label>ファイル名のルール設定</label>
        <CustomSwitch
          checked={shouldUseNameRules}
          onChange={(event) => setShouldUseNameRules(event.target.checked)}
        />
      </div>
      <Collapse in={shouldUseNameRules}>
        <div className={styles.nameRulesForm}>
          <div className={styles.nameItemsForm}>
            <div className={styles.tableSection}>
              <div className={styles.tableContainer}>
                <BaseTable
                  domLayout="autoHeight"
                  rowData={nameItems}
                  columnDefs={columns}
                  rowDragManaged
                  animateRows
                  onDragStopped={onDragStopped}
                  suppressRowHoverHighlight
                  suppressContextMenu
                  suppressCellFocus
                />
              </div>
              {showAddNameItemButton && (
                <div className={styles.nameRulesFormRow}>
                  <label className={styles.label} />
                  <Button size="smaller" color="lightGray" onClick={handleAddNameItem}>
                    追加
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.nameRulesFormRow}>
            <label className={styles.label}>凡例</label>
            {filenamePreview}
          </div>
        </div>
      </Collapse>
    </div>
  );

  return (
    <ScreenModal
      style={{
        zIndex: 9998,
      }}
      customStyles={{
        minWidth: '650px',
        maxHeight: '85%',
        border: 'var(--defaultBorder)',
        display: 'flex',
        flexDirection: 'column',
        color: 'initial',
      }}
    >
      <div className={styles['modal-header']}>
        <div className={styles.title}>
          ファイル一括ダウンロード
        </div>
      </div>
      <div className={styles['modal-body']}>
        <p>ダウンロードするファイルを選択してください。</p>
        <div className={styles.form}>
          <div className={styles.formItem}>
            <Checkbox
              className={mainStyles['text-bold']}
              label="PDFファイル"
              value={shouldIncludePdfFiles}
              onChange={handleChangeShouldIncludePdfFiles}
            />
            <div className={styles.formShift}>
              <Checkbox
                disabled={!shouldIncludePdfFiles}
                label="旧版を含む"
                value={shouldIncludePreviousVersions}
                onChange={setShouldIncludePreviousVersions}
              />
            </div>
          </div>
          <div className={styles.formItem}>
            <Checkbox
              className={mainStyles['text-bold']}
              label="添付ファイル"
              value={shouldIncludeAttachmentFiles}
              onChange={setShouldIncludeAttachmentFiles}
            />
          </div>
        </div>
        <div className={styles.info}>
          <p>
            ※選択中の
            {totalDocumentQuantity}
            件のうち、指定したファイルが存在するもののみが対象です。
          </p>
          <p>※ファイルサイズが大きい場合、ダウンロード用のzipファイルが分割されることがあります。</p>
        </div>
        {nameRulesForm}
      </div>
      <div className={styles['modal-footer']}>
        <Button size="smaller" color="lighterGray" onClick={onCancel}>キャンセル</Button>
        <Button size="smaller" disabled={submitButtonDisabled} onClick={handleSubmit}>ダウンロード</Button>
      </div>
    </ScreenModal>
  );
}

export default BatchPdfDownloadModal;
